/**
 * Restaurant URL segment format: {slug}--{uuid}
 * Delimiter is double-dash so slug can contain single dashes (e.g. "applebees-spring").
 * Lookup and auth use only the UUID; slug is cosmetic.
 */

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SEGMENT_DELIMITER = "--";

/**
 * Derive a URL-safe slug from restaurant name.
 * Lowercase, replace spaces/special chars with -, collapse repeated dashes, trim.
 */
export function slugify(name: string): string {
  if (!name || typeof name !== "string") return "store";
  return name
    .toLowerCase()
    .trim()
    .replace(/[\s]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "") || "store";
}

/**
 * Build the single path segment for restaurant routes.
 * Format: slug--uuid when slug is provided, else uuid only (for legacy/redirects).
 */
export function restaurantSegment(correlationId: string, slug?: string): string {
  if (!correlationId) return "";
  if (slug != null && slug !== "") {
    const safe = slugify(slug);
    return `${safe}${SEGMENT_DELIMITER}${correlationId}`;
  }
  return correlationId;
}

export interface ParsedRestaurantSegment {
  correlationId: string | null;
  slug?: string;
}

/**
 * Parse the restaurant path segment into correlationId (and optional slug).
 * Split on last "--"; the part after must be a valid UUID.
 * Returns { correlationId: null } on parse failure.
 */
export function parseRestaurantSegment(segment: string): ParsedRestaurantSegment {
  if (!segment || typeof segment !== "string") return { correlationId: null };
  const lastDelim = segment.lastIndexOf(SEGMENT_DELIMITER);
  if (lastDelim === -1) {
    // segment is UUID only (legacy or short link)
    const trimmed = segment.trim();
    return UUID_REGEX.test(trimmed)
      ? { correlationId: trimmed }
      : { correlationId: null };
  }
  const uuidPart = segment.slice(lastDelim + SEGMENT_DELIMITER.length).trim();
  if (!UUID_REGEX.test(uuidPart)) return { correlationId: null };
  const slugPart = segment.slice(0, lastDelim).trim();
  return {
    correlationId: uuidPart,
    slug: slugPart || undefined,
  };
}
